import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@exhousing/design-system";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Lock,
  ShieldCheck,
  Trash2,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { StatusBadge } from "@/components/StatusBadge";
import {
  type MailboxConnectionCreate,
  type TestConnectionResult,
  useDeleteMailboxConnection,
  useMailboxConnection,
  useSaveMailboxConnection,
  useTestMailbox,
} from "@/hooks/api/useInbox";

const selectClass =
  "flex h-11 w-full rounded-md border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function InboxSettingsPage() {
  const { data: connection, isLoading } = useMailboxConnection();
  const hasConnection = Boolean(connection);
  const save = useSaveMailboxConnection(hasConnection);
  const remove = useDeleteMailboxConnection();
  const test = useTestMailbox();

  const [host, setHost] = useState("");
  const [port, setPort] = useState(993);
  const [security, setSecurity] = useState<"ssl" | "starttls">("ssl");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [folder, setFolder] = useState("INBOX");
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);

  // Hydrate the form from the saved connection once it loads.
  const [hydrated, setHydrated] = useState(false);
  if (connection && !hydrated) {
    setHost(connection.host);
    setPort(connection.port);
    setSecurity(connection.security === "starttls" ? "starttls" : "ssl");
    setUsername(connection.username);
    setFolder(connection.folder);
    setHydrated(true);
  }

  const payload = (): MailboxConnectionCreate => ({
    host: host.trim(),
    port,
    security,
    username: username.trim(),
    password,
    folder: folder.trim() || "INBOX",
  });

  const onTest = () => {
    setTestResult(null);
    // Test the saved connection when nothing was re-typed; otherwise test the form.
    const target = hasConnection && !password && host === connection?.host ? "saved" : payload();
    test.mutate(target, {
      onSuccess: (r) => {
        setTestResult(r);
        if (r.ok) toast.success(`Connected — ${r.message_count ?? 0} messages in the folder`);
      },
      onError: (err) => toast.error(err.message),
    });
  };

  const onSave = (e: React.FormEvent) => {
    e.preventDefault();
    const body = payload();
    // On edit, an empty password means "keep the existing one".
    if (hasConnection && !password) delete (body as { password?: string }).password;
    save.mutate(body, {
      onSuccess: () => {
        toast.success(hasConnection ? "Mailbox updated" : "Mailbox connected");
        setPassword("");
      },
      onError: (err) => toast.error(err.message),
    });
  };

  const onDisconnect = () => {
    remove.mutate(undefined, {
      onSuccess: () => {
        toast.success("Mailbox disconnected");
        setHydrated(false);
        setHost("");
        setUsername("");
        setPassword("");
        setFolder("INBOX");
        setPort(993);
        setSecurity("ssl");
      },
      onError: (err) => toast.error(err.message),
    });
  };

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link
        to="/inbox"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Inbox
      </Link>

      <header>
        <h1 className="font-display text-3xl font-medium tracking-tight">
          Mailbox connection
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect a mailbox from your own provider. Balatin reads it to file
          correspondence against the right person.
        </p>
      </header>

      {/* Read-only reassurance — the trust contract, made visible. */}
      <div className="flex items-start gap-3 rounded-lg border border-border bg-success-soft/40 px-4 py-3">
        <ShieldCheck className="mt-0.5 size-5 shrink-0 text-success" />
        <p className="text-sm text-foreground/80">
          <span className="font-medium">Read-only and private.</span> We never send,
          move, mark as read, or delete anything. Your password is encrypted and is
          never shown again after you save it.
        </p>
      </div>

      {connection && (
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Status</CardTitle>
            <StatusBadge status={connection.status} />
          </CardHeader>
          <CardContent className="space-y-1 text-sm text-muted-foreground">
            <p>
              Last sync:{" "}
              {connection.last_synced_at
                ? new Date(connection.last_synced_at).toLocaleString("nl-NL")
                : "not yet"}
            </p>
            {connection.last_error && (
              <p className="text-danger">{connection.last_error}</p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {hasConnection ? "Edit connection" : "Connect a mailbox"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSave} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="host">IMAP server</Label>
              <Input
                id="host"
                placeholder="imap.gmail.com"
                value={host}
                onChange={(e) => setHost(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="port">Port</Label>
              <Input
                id="port"
                type="number"
                value={port}
                onChange={(e) => setPort(Number(e.target.value))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="security">Security</Label>
              <select
                id="security"
                value={security}
                onChange={(e) => setSecurity(e.target.value as "ssl" | "starttls")}
                className={selectClass}
              >
                <option value="ssl">SSL / TLS (993)</option>
                <option value="starttls">STARTTLS (143)</option>
              </select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                placeholder="agent@agency.com"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="password">
                Password
                {hasConnection && (
                  <span className="ml-2 font-normal text-muted-foreground">
                    — leave blank to keep the current one
                  </span>
                )}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  className="pl-9"
                  placeholder={hasConnection ? "••••••••" : "App password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required={!hasConnection}
                />
              </div>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="folder">Folder</Label>
              <Input
                id="folder"
                value={folder}
                onChange={(e) => setFolder(e.target.value)}
              />
            </div>

            {testResult && (
              <div
                className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm sm:col-span-2 ${
                  testResult.ok
                    ? "bg-success-soft/50 text-success"
                    : "bg-danger-soft/50 text-danger"
                }`}
              >
                {testResult.ok ? (
                  <CheckCircle2 className="size-4" />
                ) : (
                  <XCircle className="size-4" />
                )}
                {testResult.ok
                  ? `Connection OK — ${testResult.message_count ?? 0} messages.`
                  : testResult.error ?? "Could not connect."}
              </div>
            )}

            <div className="flex flex-wrap gap-2 sm:col-span-2">
              <Button type="submit" disabled={save.isPending}>
                {save.isPending
                  ? "Saving…"
                  : hasConnection
                    ? "Save changes"
                    : "Connect mailbox"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onTest}
                disabled={test.isPending || !host || !username}
              >
                {test.isPending && <Loader2 className="size-4 animate-spin" />}
                Test connection
              </Button>
              {hasConnection && (
                <Button
                  type="button"
                  variant="ghost"
                  className="ml-auto text-danger hover:text-danger"
                  onClick={onDisconnect}
                  disabled={remove.isPending}
                >
                  <Trash2 className="size-4" />
                  Disconnect
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
